import { TimeRange } from '@fullstory/proto/fs/api/segment';
import { useSelector } from 'react-redux';
import { timeRangeToDates } from './timeRange';
import { isNonNull } from '@fullstory/typeutils';
import { DateRange } from '@fullstory/util/date';
import { fullstorySelectors } from '@fullstory/redux';
const { getDynamicFilteredTimeRanges } = fullstorySelectors;

export const usePrivacyTimeRanges = (timeRange: TimeRange | null) => {
  const dynamicFilteredTimeRanges = useSelector(getDynamicFilteredTimeRanges);
  const filteredTimeRange = parseFilteredTimeRanges(dynamicFilteredTimeRanges);
  if (!timeRange)
    return {
      filteredTimeRange,
      showPrivacyFilterWarning: false,
    };
  const dateRange = timeRangeToDates(timeRange);
  if (!dateRange)
    return {
      filteredTimeRange,
      showPrivacyFilterWarning: false,
    };
  const inRange = timeRangeContainedInFilteredTimeRange(dateRange, filteredTimeRange);
  return {
    filteredTimeRange,
    showPrivacyFilterWarning: inRange && filteredTimeRange.length,
  };
};

export interface FilteredTimeRanges {
  startTime: Date;
  endTime: Date;
}
// This is for the privacy flag DynamicFilteredTimeRanges, which is set in the following format:
// "2000-01-12T15:00:00Z_2000-01-12T18:00:00Z"
// "2000-01-12T15:00:00Z_2000-01-12T18:00:00Z,2000-01-15T15:00:00Z_2000-01-17T18:00:00Z"
// It's two ranges separated by "_", and multiple can be chained together with ","
export const parseFilteredTimeRanges = (dateString: string): FilteredTimeRanges[] => {
  if (!dateString) return [];
  return dateString
    .split(',')
    .map(rangeString => {
      const [start, end] = rangeString.split('_');
      if (start === undefined || end === undefined) {
        console.error('Invalid dynamic filtered time range flag string');
        return null;
      }
      try {
        const ranges = {
          startTime: new Date(start),
          endTime: new Date(end),
        };
        if (ranges.endTime.getTime() < ranges.startTime.getTime()) {
          console.error('Dynamic filtered time range end time is before start time');
        }
        return ranges;
      } catch (e) {
        console.error(`invalid date found in dynamic filtered time range flag${e}`);
        return null;
      }
    })
    .filter(isNonNull);
};

// Helper to return true if a date is contained within one of the filtered time ranges, or false if it's not.
export const timeRangeContainedInFilteredTimeRange = (
  range: DateRange,
  timeRanges: FilteredTimeRanges[],
): boolean => {
  for (const timeRange of timeRanges) {
    if (!range.startTime || !range.endTime) continue;
    const inRange =
      range.startTime.getTime() <= timeRange.endTime.getTime() &&
      range.endTime.getTime() >= timeRange.startTime.getTime();
    if (inRange) return true;
  }
  return false;
};
